import { type Dispatch, type SetStateAction, useEffect, useState } from "react";

import { AnalyticsDataSource } from "@doitintl/cmp-models";
import { Divider, Link, Stack } from "@mui/material";

import { attributionText, budgetText } from "../../../../assets/texts";
import DataHubCheckbox from "../../../../Components/DataHubCheckbox/DataHubCheckbox";
import { useCloudAnalyticsMetadata } from "../../../../Components/hooks/cloudAnalytics/useCloudAnalyticsMetadata";
import { AttributionSelectMulti } from "../../../../Components/Selects/CloudAnalytics/AttributionSelectMulti";
import { useAttributionsContext } from "../../../../Context/AttributionsContext";
import { useCustomerContext } from "../../../../Context/CustomerContext";
import { type BudgetInfo } from "../../../../types";
import AttributionCreateDialog from "../../attributions/attributionBuilder/AttributionCreateDialog";

type Props = {
  budgetInfo: BudgetInfo;
  setBudgetInfo: Dispatch<SetStateAction<BudgetInfo>>;
  isCurrentUserEditor: boolean;
};

const ScopeSelector = ({ budgetInfo, setBudgetInfo, isCurrentUserEditor }: Props) => {
  const { attributions } = useAttributionsContext();
  const { customer: genuineCustomer } = useCustomerContext();
  const { hasDataHub } = useCloudAnalyticsMetadata();
  const [newAttributionDialogOpen, setNewAttributionDialogOpen] = useState(false);
  const [newAttributionId, setNewAttributionId] = useState("");

  useEffect(() => {
    if (newAttributionId) {
      const attribution = attributions.find((a) => a.ref.id === newAttributionId);
      if (attribution) {
        setBudgetInfo((prev) => ({ ...prev, scope: prev.scope ? [...prev.scope, attribution] : [attribution] }));
      }
      setNewAttributionId("");
    }
  }, [attributions, newAttributionId, setBudgetInfo]);

  if (budgetInfo.scope.length === 0) {
    return null;
  }
  return (
    <Stack>
      <AttributionSelectMulti
        attributions={attributions}
        onChange={(newScope) => {
          setBudgetInfo((prev) => ({ ...prev, scope: newScope }));
        }}
        scope={budgetInfo.scope}
        sx={{ width: "100%" }}
        disabled={!isCurrentUserEditor}
        textFieldProps={{
          required: false,
          label: budgetText.BUDGET_SCOPE,
          placeholder: budgetText.SELECT_ALLOCATION,
          // InputLabelProps - needed to override the default shrink behavior of the label
          InputLabelProps: {},
        }}
      />
      <Stack
        direction="row"
        divider={<Divider orientation="vertical" sx={{ height: "auto", mr: 2 }} />}
        sx={{
          mt: 1,
          mb: 1,
        }}
      >
        <DataHubCheckbox
          checked={budgetInfo.dataSource === AnalyticsDataSource.BILLING_DATAHUB}
          customerId={genuineCustomer.id}
          dataSource={budgetInfo.dataSource}
          disabled={!isCurrentUserEditor || !hasDataHub}
          onChange={(_, checked) => {
            setBudgetInfo((prev) => ({
              ...prev,
              dataSource: checked ? AnalyticsDataSource.BILLING_DATAHUB : AnalyticsDataSource.BILLING,
            }));
          }}
          showClouds={false}
        />
        <AttributionCreateDialog
          onCancel={() => {
            setNewAttributionDialogOpen(false);
          }}
          onSuccess={setNewAttributionId}
          open={newAttributionDialogOpen}
        >
          <Link
            color="inherit"
            variant="caption"
            underline="always"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              if (isCurrentUserEditor) {
                setNewAttributionDialogOpen(true);
              }
            }}
            sx={!isCurrentUserEditor ? { pointerEvents: "none", opacity: 0.5 } : {}}
          >
            {attributionText.CREATE_NEW_ATTRIBUTION}
          </Link>
        </AttributionCreateDialog>
      </Stack>
    </Stack>
  );
};

export default ScopeSelector;
